import React, { useState, useRef, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Checkbox } from '@/shared/components/Checkbox';
import { DatePickerField } from '@/shared/components/DatePickerField';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import type { FormBuilderState, FormField } from '../../types';
import { TASK_FORM_LABELS } from '../../constants/labels';
import { TASK_FORM_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import UnitTextField from '../../components/UnitTextField';
import PriceCurrencyField from '../../components/PriceCurrencyField';
import GaugeDisplay from '../../components/GaugeDisplay';

// ─── PreviewField ─────────────────────────────────────────────────────────────

const PreviewField: React.FC<{ field: FormField; disabled?: boolean }> = ({
  field,
  disabled = false,
}) => {
  const [textValue, setTextValue] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [rangeStart, setRangeStart] = useState<Date | undefined>(undefined);
  const [rangeEnd, setRangeEnd] = useState<Date | undefined>(undefined);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const intervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {}
  );

  useEffect(() => {
    const intervals = intervalsRef.current;
    return () => Object.values(intervals).forEach(clearInterval);
  }, []);

  const startFakeProgress = useCallback((file: File) => {
    const DURATION = 1500;
    const INTERVAL = 50;
    const steps = DURATION / INTERVAL;
    const stepSize = Math.max(1, file.size / steps);
    let current = 0;
    setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
    const id = setInterval(() => {
      current += stepSize;
      if (current >= file.size) {
        clearInterval(id);
        delete intervalsRef.current[file.name];
        setUploadProgress((prev) => {
          const { [file.name]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setUploadProgress((prev) => ({ ...prev, [file.name]: current }));
      }
    }, INTERVAL);
    intervalsRef.current[file.name] = id;
  }, []);

  const optLabel = !field.required ? (
    <>
      {field.label}{' '}
      <span
        style={{
          fontSize: 12,
          lineHeight: '16px',
          letterSpacing: '0.4px',
          color: '#474A4C',
        }}
      >
        ({TASK_FORM_LABELS.FIELD_OPTIONAL})
      </span>
    </>
  ) : (
    field.label
  );

  switch (field.type) {
    case 'text':
      return (
        <TextField
          label={optLabel}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          fullWidth
          disabled={disabled}
        />
      );

    case 'price':
      return (
        <PriceCurrencyField
          label={optLabel}
          value={textValue}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9.]/g, '');
            setTextValue(raw.match(/^\d{0,10}(\.\d{0,2})?/)?.[0] ?? '');
          }}
          fullWidth
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <Textarea
          label={optLabel}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value.slice(0, 500))}
          maxLength={500}
          fullWidth
          rows={3}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <TextField
          label={optLabel}
          value={textValue}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9.-]/g, '');
            setTextValue(raw.match(/^-?\d{0,10}(\.\d{0,2})?/)?.[0] ?? '');
          }}
          fullWidth
          disabled={disabled}
          {...(field.unit
            ? {
                trailingIcon: (
                  <span className="text-sm text-[#474A4C] pr-3">
                    {field.unit}
                  </span>
                ),
              }
            : {})}
        />
      );

    case 'number_range': {
      const numericValue = textValue === '' ? null : parseFloat(textValue);
      return (
        <div className="flex flex-col gap-3">
          <div>
            <UnitTextField
              label={optLabel}
              value={textValue}
              onChange={(e) => {
                const raw = e.target.value
                  .replace(
                    field.min != null && field.min >= 0
                      ? /[^0-9]/g
                      : /[^0-9-]/g,
                    ''
                  )
                  .slice(0, 10);

                if (raw === '' || raw === '-') {
                  setTextValue(raw);
                  return;
                }

                const num = parseFloat(raw);
                if (field.max != null && num > field.max) {
                  return;
                }

                setTextValue(raw);
              }}
              onBlur={() => {
                if (textValue === '' || textValue === '-') {
                  return;
                }
                const num = parseFloat(textValue);
                if (field.min != null && num < field.min) {
                  setTextValue(String(field.min));
                }
              }}
              unit={field.unit ?? ''}
              fullWidth
              disabled={disabled}
            />
          </div>

          <GaugeDisplay
            value={numericValue}
            min={field.min}
            max={field.max}
            unit={field.unit}
          />
        </div>
      );
    }

    case 'date':
      return (
        <DatePickerField
          label={optLabel}
          mode="single"
          value={dateValue}
          onChange={setDateValue}
          disabled={disabled}
          labelFontSize={16}
        />
      );

    case 'date_range':
      return (
        <DatePickerField
          label={optLabel}
          mode="range"
          startDate={rangeStart}
          endDate={rangeEnd}
          onRangeChange={(start, end) => {
            setRangeStart(start);
            setRangeEnd(end);
          }}
          labelFontSize={16}
          disabled={disabled}
        />
      );

    case 'dropdown': {
      const items = (field.options ?? [])
        .filter((o) => o.label)
        .map((o) => ({ id: o.id, label: o.label }));

      if (field.multiSelect) {
        const selectedLabels = items
          .filter((i) => checkedIds.includes(i.id))
          .map((i) => i.label)
          .join(', ');
        return (
          <Dropdown
            items={items}
            selectedId={checkedIds}
            multiSelect
            showCheckboxes
            onItemClick={
              disabled
                ? () => {}
                : (id) =>
                    setCheckedIds((prev) =>
                      prev.includes(id)
                        ? prev.filter((x) => x !== id)
                        : [...prev, id]
                    )
            }
            menuFullWidth
            placement="bottom-start"
            autoMaxHeight
            trigger={({ open, toggle }) => (
              <DropDownButton
                label={optLabel}
                value={selectedLabels}
                open={open}
                toggle={disabled ? () => {} : toggle}
                disabled={disabled}
              />
            )}
          />
        );
      }

      const selectedLabel = items.find((i) => i.id === selectedId)?.label ?? '';
      return (
        <Dropdown
          items={items}
          selectedId={selectedId}
          onItemClick={disabled ? () => {} : (id) => setSelectedId(id)}
          menuFullWidth
          placement="bottom-start"
          autoMaxHeight
          trigger={({ open, toggle }) => (
            <DropDownButton
              label={optLabel}
              value={selectedLabel}
              open={open}
              toggle={disabled ? () => {} : toggle}
              disabled={disabled}
            />
          )}
        />
      );
    }

    case 'radio':
      return (
        <div className="flex flex-col gap-2">
          <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
          {(field.options ?? [])
            .filter((o) => o.label)
            .map((opt) => (
              <label
                key={opt.id}
                className={`flex items-center gap-2 py-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              >
                <input
                  type="radio"
                  name={field.id}
                  value={opt.id}
                  checked={selectedId === opt.id}
                  onChange={() => setSelectedId(opt.id)}
                  disabled={disabled}
                  className={`w-4 h-4 accent-[#2A4455] ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <span className="text-bodyMedium text-[#181A1B] align-middle">
                  {opt.label}
                </span>
              </label>
            ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="flex flex-col gap-2">
          <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
          {(field.options ?? [])
            .filter((o) => o.label)
            .map((opt) => (
              <Checkbox
                key={opt.id}
                label={opt.label}
                labelClassName="!text-bodyMedium !text-[#181A1B]"
                checked={checkedIds.includes(opt.id)}
                disabled={disabled}
                onChange={(checked) =>
                  setCheckedIds((prev) =>
                    checked
                      ? [...prev, opt.id]
                      : prev.filter((id) => id !== opt.id)
                  )
                }
              />
            ))}
        </div>
      );

    case 'attachment': {
      const allowedTypes = (field.acceptedFileTypes ?? []).filter(
        (t): t is FileType => Object.values(FileType).includes(t as FileType)
      );
      const limitsHint = [
        field.maxFiles != null ? `Maks. ${field.maxFiles} plików` : null,
        field.maxFileSizeMb != null ? `${field.maxFileSizeMb} MB każdy` : null,
      ]
        .filter(Boolean)
        .join(', ');
      return (
        <div className="flex flex-col gap-1.5">
          <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
          <FileUpload
            onFileSelect={
              disabled
                ? () => {}
                : (newFiles) => {
                    const prevNames = new Set(files.map((f) => f.name));
                    const newNames = new Set(newFiles.map((f) => f.name));
                    newFiles
                      .filter((f) => !prevNames.has(f.name))
                      .forEach(startFakeProgress);
                    [...prevNames]
                      .filter((name) => !newNames.has(name))
                      .forEach((name) => {
                        if (intervalsRef.current[name]) {
                          clearInterval(intervalsRef.current[name]);
                          delete intervalsRef.current[name];
                        }
                        setUploadProgress((prev) => {
                          const { [name]: _, ...rest } = prev;
                          return rest;
                        });
                      });
                    setFiles(newFiles);
                    if (newFiles.length > 0) {
                      setFileError('');
                    }
                  }
            }
            selectedFiles={files}
            allowedTypes={
              allowedTypes.length > 0 ? allowedTypes : Object.values(FileType)
            }
            {...(field.maxFiles != null ? { maxFiles: field.maxFiles } : {})}
            onError={setFileError}
            error={Boolean(fileError)}
            errorMessage={fileError || undefined}
            uploadProgress={uploadProgress}
            fullWidth
            disabled={disabled}
          />
          {limitsHint && <p className="text-xs text-[#474A4C]">{limitsHint}</p>}
        </div>
      );
    }

    default:
      return null;
  }
};

function isFullWidth(type: string): boolean {
  return ['textarea', 'radio', 'checkbox', 'attachment'].includes(type);
}

// ─── FormPreview ──────────────────────────────────────────────────────────────

export interface FormPreviewProps {
  formState: FormBuilderState;
  isSubmitting?: boolean;
  showBanner?: boolean;
}

const FormPreview: React.FC<FormPreviewProps> = ({
  formState,
  isSubmitting = false,
  showBanner = true,
}) => {
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <>
      {/* Preview note banner */}
      {showBanner && !bannerDismissed && (
        <div className="flex items-center w-full gap-3 bg-[#EAF3FF] rounded-lg px-4 py-3">
          <InformationCircleIcon className="w-5 h-5 text-[#1E7CE8] shrink-0" />
          <p className="text-sm text-[#1E7CE8] flex-1">
            <strong>{TL.PREVIEW_NOTE_PREFIX.polish}</strong>{' '}
            {TL.PREVIEW_NOTE.polish}
          </p>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="text-[#1E7CE8] hover:text-[#1565C0] shrink-0 hover:cursor-pointer"
            aria-label="Zamknij"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Content */}
      <div
        className="flex flex-col gap-7 w-[892px] p-4 rounded-[8px] border border-[#DCDDE0] self-center"
        style={{
          boxShadow: '0px 1px 3px 0px #00000026, 0px 4px 8px 3px #00000014',
        }}
      >
        {/* Form name & description */}
        {(formState.name || formState.description) && (
          <div className="flex flex-col gap-2">
            {formState.name && (
              <h2
                data-testid="form-preview-name"
                className="text-titleLarge text-[#181A1B] break-words"
              >
                {formState.name}
              </h2>
            )}
            {formState.description && (
              <p
                data-testid="form-preview-description"
                className="text-bodyMedium text-[#474A4C] break-words"
              >
                {formState.description}
              </p>
            )}
          </div>
        )}

        {/* Sections */}
        {formState.sections.map((section, sectionIdx) => (
          <div
            key={section.id}
            data-testid={`form-preview-section-${sectionIdx}`}
            className="flex flex-col gap-4 pb-7 border-b border-[#DCDDE0]"
          >
            <div>
              <h3
                data-testid={`form-preview-section-name-${sectionIdx}`}
                className="text-titleDefault text-[#474A4C] break-words"
              >
                {section.name}
              </h3>
              {section.description && (
                <p className="text-bodyMedium text-[#474A4C] mt-1 break-words">
                  {section.description}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              {section.fields.map((field) => (
                <div
                  key={field.id}
                  className={isFullWidth(field.type) ? 'col-span-2' : ''}
                >
                  <div
                    className={
                      isSubmitting
                        ? 'opacity-50 pointer-events-none select-none'
                        : ''
                    }
                  >
                    <PreviewField field={field} disabled={isSubmitting} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Signature section */}
        {formState.signatureRequired && (
          <div
            className={`flex flex-col gap-3${isSubmitting ? ' opacity-50 pointer-events-none select-none' : ''}`}
          >
            <h3 className="text-titleDefault text-[#474A4C]">
              {TASK_FORM_LABELS.SIGNATURE_SECTION}
            </h3>
            <div className="border-2 border-dashed border-[#DCDDE0] rounded-lg h-44 flex items-center justify-center bg-[#FFFFFF]">
              <span className="text-sm text-[#9B9EA0]">Złóż podpis tutaj</span>
            </div>
            <p className="text-xs text-[#474A4C]">
              {TL.SIGNATURE_INSTRUCTION.polish}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default FormPreview;
